/**
 * Segmentation: split the strokes of a written answer into characters.
 *
 * Answers are written left to right on one line, so characters are found by
 * horizontal extent: strokes whose x-ranges overlap substantially belong to
 * the same character (two-stroke 4, 5 with a separate bar, 7 with a bar,
 * two-loop 8, 1 with a base). Tiny marks are kept apart as decimal points
 * unless they sit inside another character's upper body.
 *
 * A stacked fraction (digits above AND below a horizontal bar) is detected
 * first and split into numerator / bar / denominator rows.
 */
import { median } from '@dropstack/core';
import type { HwStroke } from '../../contracts.ts';
import { type Box, type Polyline, type Pt, boundsOf, dist, pathLength, resampleLine, unionBox, xOverlap } from './geometry.ts';

/** Tunables for segmentation (fractions of the reference character height H unless noted). */
export const SEGMENT = {
  /** Strokes at least this fraction of the tallest stroke's height count towards H. */
  TALL_FRACTION: 0.35,
  /** A stroke whose larger side is below this × H is "tiny" (dot candidate). */
  TINY: 0.22,
  /** Tiny-stroke size in px used when there is no reference height (a lone mark). */
  TINY_ABS_PX: 12,
  /** Minimum effective width of a stroke for overlap tests (a vertical line has width 0). */
  MIN_WIDTH: 0.12,
  /** Merge two strokes when their x-overlap covers this share of the narrower one… */
  MERGE_OVERLAP: 0.45,
  /** …or when they touch/cross (closer than TOUCH × H) and overlap by at least this share (4 stem, 1 base). */
  TOUCH_OVERLAP: 0.1,
  TOUCH: 0.1,
  /** A tiny stroke joins a character it overlaps only if it sits above this share of its height (7 bar, 1 flag). */
  TINY_JOIN_MAX_Y: 0.7,
  /** Fraction bar: chord / path length at least this straight… */
  BAR_STRAIGHTNESS: 0.9,
  /** …and within this many degrees of horizontal. */
  BAR_MAX_ANGLE: 20,
  /** Strokes above/below the bar may poke across it by this share of the bar width. */
  BAR_TOLERANCE: 0.1,
  /** Horizontal margin around the bar (share of its width) that still counts as "over the bar". */
  BAR_MARGIN: 0.15,
} as const;

export interface StrokeInfo {
  /** Index into the recogniser's input array. */
  index: number;
  points: HwStroke;
  /** Evenly thinned copy used for proximity tests. */
  coarse: Pt[];
  box: Box;
  length: number;
  /** Chord / path length: 1 for a perfectly straight stroke. */
  straightness: number;
  /** Chord angle from horizontal, 0–90°. */
  angle: number;
}

export interface CharGroup {
  strokeIndices: number[];
  box: Box;
}

export interface Row {
  groups: CharGroup[];
  /** Reference character height H for this row; null when nothing full-size was written. */
  lineHeight: number | null;
}

export type Segmentation =
  | { kind: 'row'; row: Row }
  | { kind: 'stacked'; before: Row; numerator: Row; bar: CharGroup; denominator: Row; after: Row };

export function describeStroke(points: HwStroke, index: number): StrokeInfo {
  const box = boundsOf([points]);
  const length = pathLength(points);
  const first = points[0]!;
  const last = points[points.length - 1]!;
  const chord = dist(first, last);
  const straightness = length > 0 ? chord / length : 1;
  const angle = chord > 0 ? (Math.atan2(Math.abs(last.y - first.y), Math.abs(last.x - first.x)) * 180) / Math.PI : 0;
  const coarse = resampleLine(points, Math.min(points.length, 24));
  return { index, points, coarse, box, length, straightness, angle };
}

const maxDim = (b: Box) => Math.max(b.w, b.h);
const centerX = (b: Box) => b.x + b.w / 2;
const centerY = (b: Box) => b.y + b.h / 2;
const isFlat = (b: Box) => b.w > 2.5 * b.h;

/** Reference height from strokes: median height of the tall, non-flat ones. */
function strokeLineHeight(strokes: readonly StrokeInfo[]): number | null {
  const maxH = Math.max(0, ...strokes.map((s) => s.box.h));
  if (maxH <= 0) return null;
  const tall = strokes.filter((s) => s.box.h >= SEGMENT.TALL_FRACTION * maxH && !isFlat(s.box)).map((s) => s.box.h);
  return tall.length ? median(tall) : null;
}

/** Reference height from grouped characters (more robust than per-stroke). */
function groupLineHeight(groups: readonly CharGroup[], tinyLimit: number): number | null {
  const sized = groups.filter((g) => maxDim(g.box) >= tinyLimit && !isFlat(g.box));
  const maxH = Math.max(0, ...sized.map((g) => g.box.h));
  const tall = sized.filter((g) => g.box.h >= 0.4 * maxH).map((g) => g.box.h);
  return tall.length && maxH > 0 ? median(tall) : null;
}

/** Share of the narrower stroke's (effective) width that overlaps the other. */
function overlapRatio(a: Box, b: Box, minWidth: number): number {
  const widen = (box: Box): Box =>
    box.w >= minWidth ? box : { x: centerX(box) - minWidth / 2, y: box.y, w: minWidth, h: box.h };
  const wa = widen(a);
  const wb = widen(b);
  return xOverlap(wa, wb) / Math.min(wa.w, wb.w);
}

function segmentsCross(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
  const cross = (o: Pt, p: Pt, q: Pt) => (p.x - o.x) * (q.y - o.y) - (p.y - o.y) * (q.x - o.x);
  const d1 = cross(c, d, a);
  const d2 = cross(c, d, b);
  const d3 = cross(a, b, c);
  const d4 = cross(a, b, d);
  return d1 * d2 < 0 && d3 * d4 < 0;
}

/** True when two polylines cross or pass within `gap` of each other. */
function touching(a: Polyline, b: Polyline, gap: number): boolean {
  for (const p of a) for (const q of b) if (dist(p, q) <= gap) return true;
  for (let i = 1; i < a.length; i++) {
    for (let j = 1; j < b.length; j++) if (segmentsCross(a[i - 1]!, a[i]!, b[j - 1]!, b[j]!)) return true;
  }
  return false;
}

/** Group one line of strokes into characters, ordered left to right. */
export function buildRow(strokes: readonly StrokeInfo[]): Row {
  if (!strokes.length) return { groups: [], lineHeight: null };
  const h0 = strokeLineHeight(strokes);
  const tinyLimit = h0 !== null ? SEGMENT.TINY * h0 : SEGMENT.TINY_ABS_PX;
  const scale = h0 ?? Math.max(...strokes.map((s) => maxDim(s.box)));
  const minWidth = SEGMENT.MIN_WIDTH * scale;
  const belongTogether = (a: StrokeInfo, b: StrokeInfo): boolean => {
    const overlap = overlapRatio(a.box, b.box, minWidth);
    if (overlap >= SEGMENT.MERGE_OVERLAP) return true;
    return overlap >= SEGMENT.TOUCH_OVERLAP && touching(a.coarse, b.coarse, SEGMENT.TOUCH * scale);
  };

  const isTiny = (s: StrokeInfo) => maxDim(s.box) < tinyLimit;
  const big = strokes.filter((s) => !isTiny(s));
  const tiny = strokes.filter(isTiny);

  // Union-find over the full-size strokes by horizontal overlap.
  const parent = big.map((_, i) => i);
  const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i]!)));
  for (let i = 0; i < big.length; i++) {
    for (let j = i + 1; j < big.length; j++) {
      if (belongTogether(big[i]!, big[j]!)) parent[find(i)] = find(j);
    }
  }
  const byRoot = new Map<number, StrokeInfo[]>();
  big.forEach((s, i) => {
    const r = find(i);
    byRoot.set(r, [...(byRoot.get(r) ?? []), s]);
  });
  const groups: CharGroup[] = [...byRoot.values()].map((members) => ({
    strokeIndices: members.map((s) => s.index),
    box: boundsOf(members.map((s) => s.points)),
  }));

  // Tiny marks: part of a character (flag, bar, serif) when inside its upper body, otherwise a dot.
  for (const s of tiny) {
    const host = groups.find(
      (g) =>
        overlapRatio(s.box, g.box, minWidth) >= 0.5 && centerY(s.box) < g.box.y + SEGMENT.TINY_JOIN_MAX_Y * g.box.h,
    );
    if (host) {
      host.strokeIndices.push(s.index);
      host.box = unionBox(host.box, s.box);
    } else {
      groups.push({ strokeIndices: [s.index], box: s.box });
    }
  }

  for (const g of groups) g.strokeIndices.sort((a, b) => a - b);
  groups.sort((a, b) => centerX(a.box) - centerX(b.box));
  // A lone mark can't be measured against itself: leave the scale unknown so size rules use absolute px.
  const lineHeight = groups.length > 1 ? (groupLineHeight(groups, tinyLimit) ?? h0) : null;
  return { groups, lineHeight };
}

/**
 * Look for a stacked fraction: a straight, near-horizontal stroke with other
 * strokes entirely above AND entirely below it within its horizontal span,
 * and nothing crossing it there.
 */
function findStacked(strokes: readonly StrokeInfo[]): Extract<Segmentation, { kind: 'stacked' }> | null {
  const bars = strokes
    .filter((s) => s.straightness >= SEGMENT.BAR_STRAIGHTNESS && s.angle <= SEGMENT.BAR_MAX_ANGLE && isFlat(s.box))
    .sort((a, b) => b.box.w - a.box.w);
  for (const bar of bars) {
    const lo = bar.box.x - SEGMENT.BAR_MARGIN * bar.box.w;
    const hi = bar.box.x + (1 + SEGMENT.BAR_MARGIN) * bar.box.w;
    const barY = centerY(bar.box);
    const tol = SEGMENT.BAR_TOLERANCE * bar.box.w;
    const others = strokes.filter((s) => s !== bar);
    const over = others.filter((s) => centerX(s.box) >= lo && centerX(s.box) <= hi);
    const above = over.filter((s) => s.box.y + s.box.h <= barY + tol);
    const below = over.filter((s) => s.box.y >= barY - tol);
    if (!above.length || !below.length || above.length + below.length !== over.length) continue;
    // The bar should span its numerator and denominator reasonably.
    const numBox = boundsOf(above.map((s) => s.points));
    const denBox = boundsOf(below.map((s) => s.points));
    if (bar.box.w < 0.5 * Math.max(numBox.w, denBox.w)) continue;
    return {
      kind: 'stacked',
      before: buildRow(others.filter((s) => centerX(s.box) < lo)),
      numerator: buildRow(above),
      bar: { strokeIndices: [bar.index], box: bar.box },
      denominator: buildRow(below),
      after: buildRow(others.filter((s) => centerX(s.box) > hi)),
    };
  }
  return null;
}

/** Segment raw strokes (empty strokes are ignored). */
export function segment(strokes: readonly HwStroke[]): Segmentation {
  const infos = strokes.map((s, i) => (s.length ? describeStroke(s, i) : null)).filter((s) => s !== null);
  return findStacked(infos) ?? { kind: 'row', row: buildRow(infos) };
}
